import { useRef, useCallback, useEffect } from 'react';

export const useAudio = () => {
    const audioCtxRef = useRef(null);
    const pingBufferRef = useRef(null);

    const getAudioCtx = () => {
        if (!audioCtxRef.current) {
            audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return audioCtxRef.current;
    };

    // Preload the metal hit sound
    useEffect(() => {
        const loadPing = async () => {
            try {
                const ctx = getAudioCtx();
                const response = await fetch('/assets/sounds/metal-hit.mp3');
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
                pingBufferRef.current = audioBuffer;
            } catch (err) {
                console.error("Failed to load metal-hit.mp3:", err);
            }
        };
        loadPing();
    }, []);

    const beep = useCallback((freq = 900, dur = 0.15) => {
        const ctx = getAudioCtx();
        if (ctx.state === 'suspended') ctx.resume();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = freq;
        g.gain.value = 0.15;
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + dur);
    }, []);

    const ping = useCallback(() => {
        const ctx = getAudioCtx();
        if (ctx.state === 'suspended') ctx.resume();

        if (pingBufferRef.current) {
            const source = ctx.createBufferSource();
            source.buffer = pingBufferRef.current;
            const g = ctx.createGain();
            g.gain.value = 0.4; // Slightly louder requested
            source.connect(g);
            g.connect(ctx.destination);
            source.start(0);
        } else {
            // Fallback to generated clonk if file didn't load yet
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine';
            o.frequency.setValueAtTime(400, ctx.currentTime);
            o.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.1);
            g.gain.setValueAtTime(0.2, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
            o.connect(g);
            g.connect(ctx.destination);
            o.start();
            o.stop(ctx.currentTime + 0.2);
        }
    }, []);

    return { beep, ping };
};
